import React, { useContext, useEffect, useState } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card } from 'react-bootstrap';
import { UserContext } from '../Contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaUser, FaEnvelope, FaGlobe, FaUsers, FaIdCard, FaSave, FaArrowLeft, FaTimesCircle, FaCheckCircle } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const EditUserProfile = () => {
    const navigate = useNavigate();
    const { user, setUser } = useContext(UserContext);
    const [formData, setFormData] = useState({
        username: '',
        firstname: '',
        lastname: '',
        email: '',
        password: '',
        ethnicity: '',
        country: '',
        confirmPassword: ''
    });

    const [validated, setValidated] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    useEffect(() => {
        // Check if user is populated and has a token
        if (!user?.token) {
            // Perform the redirect or other action if token is missing
            navigate('/login_required');
        }
    }, [navigate, user?.token]);

    useEffect(() => {
        setFormData({
            username: user?.username,
            firstname: user?.first_name,
            lastname: user?.last_name,
            email: user?.email_address,
            password: '',
            ethnicity: user?.ethnicity,
            confirmPassword: '',
            country: user?.country,
        })
    }, [user]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        if (form.checkValidity() === false) {
            e.stopPropagation();
        } else {
            setError('');
            // Handle successful signup here (e.g., send data to backend)
        }
        setValidated(true);

        const payload = {
            first_name: formData.firstname,
            last_name: formData.lastname,
            username: formData.username,
            email: formData.email,
            role: user?.role,
            subscription_plan: user?.subscription_name,
            password: formData.password,
            ethnicity: formData.ethnicity,
            country: formData.country
        };

        const response = await fetch(`${process.env.REACT_APP_API_URL}users/users/` + user?.uuid, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${user?.token}`,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            console.log('Network response was not ok');
        }

        const result = await response.json();

        if (result?.user?.uuid) {
            setMessage('Account Updated Succesfully')
            setUser({
                ...result?.user,
                ...result?.subscription
            });
        }
    };

    const styles = {
        pageContainer: {
            backgroundColor: '#f8f9fa',
            minHeight: '100vh',
            padding: '2rem 0',
        },
        card: {
            border: 'none',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            background: '#ffffff',
        },
        header: {
            background: 'linear-gradient(135deg, #1a365d 0%, #2C5282 100%)',
            padding: '2rem',
            color: 'white',
            borderBottom: 'none',
        },
        headerTitle: {
            fontSize: '1.75rem',
            fontWeight: '700',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
        },
        headerSubtitle: {
            fontSize: '1rem',
            opacity: '0.9',
        },
        formContainer: {
            padding: '2rem',
        },
        formGroup: {
            marginBottom: '1.5rem',
        },
        formLabel: {
            fontSize: '0.9rem',
            fontWeight: '600',
            color: '#4a5568',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
        },
        formControl: {
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '0.75rem',
            fontSize: '1rem',
            transition: 'all 0.2s ease',
            backgroundColor: '#f8fafc',
            '&:focus': {
                borderColor: '#2C5282',
                boxShadow: '0 0 0 3px rgba(44,82,130,0.1)',
            },
            '&:disabled': {
                backgroundColor: '#edf2f7',
                cursor: 'not-allowed',
            },
        },
        submitButton: {
            background: 'linear-gradient(135deg, #2C5282 0%, #1a365d 100%)',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'transform 0.2s ease',
            '&:hover': {
                transform: 'translateY(-2px)',
            },
        },
        backButton: {
            color: '#2C5282',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1rem',
            transition: 'color 0.2s ease',
            '&:hover': {
                color: '#1a365d',
            },
        },
        alert: {
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
        },
        icon: {
            fontSize: '1.2rem',
        },
    };

    return (
        <div style={styles.pageContainer}>
            <Container>
                <Row className="justify-content-center">
                    <Col md={8} lg={6}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <Link to="/user_profile" style={styles.backButton}>
                                <FaArrowLeft /> Back to Profile
                            </Link>

                            <Card style={styles.card}>
                                <div style={styles.header}>
                                    <div style={styles.headerTitle}>
                                        <FaUser />
                                        Edit Profile
                                    </div>
                                    <div style={styles.headerSubtitle}>
                                        Update your personal information
                                    </div>
                                </div>

                                <div style={styles.formContainer}>
                                    {error && (
                                        <Alert variant="danger" style={styles.alert}>
                                            <FaTimesCircle /> {error}
                                        </Alert>
                                    )}
                                    {message && (
                                        <Alert variant="success" style={styles.alert}>
                                            <FaCheckCircle /> {message}
                                        </Alert>
                                    )}

                                    <Form noValidate validated={validated} onSubmit={handleSubmit}>
                                        <motion.div
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.3, delay: 0.1 }}
                                        >
                                            <Form.Group className="mb-4" controlId="formUsername">
                                                <Form.Label style={styles.formLabel}>
                                                    <FaIdCard style={styles.icon} /> Username
                                                </Form.Label>
                                                <Form.Control
                                                    required
                                                    type="text"
                                                    name="username"
                                                    placeholder="Enter username"
                                                    value={formData.username}
                                                    onChange={handleChange}
                                                    disabled
                                                    style={styles.formControl}
                                                />
                                            </Form.Group>
                                        </motion.div>

                                        <Row className="mb-4">
                                            <Col md={6}>
                                                <motion.div
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ duration: 0.3, delay: 0.2 }}
                                                >
                                                    <Form.Group controlId="formFirstname">
                                                        <Form.Label style={styles.formLabel}>
                                                            <FaUser style={styles.icon} /> First Name
                                                        </Form.Label>
                                                        <Form.Control
                                                            required
                                                            type="text"
                                                            name="firstname"
                                                            placeholder="Enter first name"
                                                            value={formData.firstname}
                                                            onChange={handleChange}
                                                            style={styles.formControl}
                                                        />
                                                    </Form.Group>
                                                </motion.div>
                                            </Col>
                                            <Col md={6}>
                                                <motion.div
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ duration: 0.3, delay: 0.3 }}
                                                >
                                                    <Form.Group controlId="formLastname">
                                                        <Form.Label style={styles.formLabel}>
                                                            <FaUser style={styles.icon} /> Last Name
                                                        </Form.Label>
                                                        <Form.Control
                                                            required
                                                            type="text"
                                                            name="lastname"
                                                            placeholder="Enter last name"
                                                            value={formData.lastname}
                                                            onChange={handleChange}
                                                            style={styles.formControl}
                                                        />
                                                    </Form.Group>
                                                </motion.div>
                                            </Col>
                                        </Row>

                                        <motion.div
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.3, delay: 0.4 }}
                                        >
                                            <Form.Group className="mb-4" controlId="formCountry">
                                                <Form.Label style={styles.formLabel}>
                                                    <FaGlobe style={styles.icon} /> Country of Origin
                                                </Form.Label>
                                                <Form.Control
                                                    required
                                                    type="text"
                                                    name="country"
                                                    placeholder="Enter country"
                                                    value={formData.country}
                                                    onChange={handleChange}
                                                    style={styles.formControl}
                                                />
                                            </Form.Group>
                                        </motion.div>

                                        <motion.div
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.3, delay: 0.5 }}
                                        >
                                            <Form.Group className="mb-4" controlId="formEthnicity">
                                                <Form.Label style={styles.formLabel}>
                                                    <FaUsers style={styles.icon} /> Ethnicity
                                                </Form.Label>
                                                <Form.Control
                                                    required
                                                    type="text"
                                                    name="ethnicity"
                                                    placeholder="Enter ethnicity"
                                                    value={formData.ethnicity}
                                                    onChange={handleChange}
                                                    style={styles.formControl}
                                                />
                                            </Form.Group>
                                        </motion.div>

                                        <motion.div
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.3, delay: 0.6 }}
                                        >
                                            <Form.Group className="mb-4" controlId="formEmail">
                                                <Form.Label style={styles.formLabel}>
                                                    <FaEnvelope style={styles.icon} /> Email address
                                                </Form.Label>
                                                <Form.Control
                                                    required
                                                    type="email"
                                                    name="email"
                                                    placeholder={formData?.email}
                                                    value={formData?.email}
                                                    onChange={handleChange}
                                                    disabled
                                                    style={styles.formControl}
                                                />
                                            </Form.Group>
                                        </motion.div>

                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, delay: 0.7 }}
                                            className="d-grid"
                                        >
                                            <Button
                                                variant="primary"
                                                type="submit"
                                                style={styles.submitButton}
                                                className="mt-4"
                                            >
                                                <FaSave /> Update Profile
                                            </Button>
                                        </motion.div>
                                    </Form>
                                </div>
                            </Card>
                        </motion.div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default EditUserProfile;
